﻿import React from "react";
import _, {
    isNull,
    isNumber,
    isEmpty,
    cloneDeep,
    find,
    sortBy,
    reverse,
    isNaN,
    findIndex,
    remove,
    indexOf,
} from "lodash";
import styled from "styled-components";
import { CONTAINER } from "../Colors";
import { TableBody } from "./TableBody";
import { TableHeader } from "./TableHeader";
import { VariableSizeGrid } from "react-window";
import { useMemo } from "react";

const TableDiv = styled.div`
    display: ${(props) => (props.theme.hidden ? "none" : "flex")};
    flex-direction: column;
    height: 100%;
    width: 100%;
    background-color: ${CONTAINER};
`;

enum SortOption {
    default = 0,
    asc = 1,
    dsc = 2,
}

export const orderCharLookup = {
    0: ``,
    1: ` ↑`,
    2: ` ↓`,
};

/**
 * Column Definition
 *
 */
export interface IColumn {
    /* Type of column */
    type: string;

    /* Display name of the column */
    displayName?: string;

    /* Column width */
    width: number;

    /* Minimum width which the item should be */
    minWidth?: number;

    /* Unique identifier belonging to a column */
    columnKey: number;

    customReOrder?: (value: unknown) => void;

    disableReOrder?: boolean;
}

export interface IValueColumn extends IColumn {
    /* Type of column */
    type: "value";

    /* Row property name */
    selector: string | ((row) => string);
}

export interface IMenuColumn extends IColumn {
    /* Type of column */
    type: "menu";

    /* Menu items */
    menuItems: { label: string; onClick: (row: any) => void }[];
}

export interface IButtonColumn extends IColumn {
    /* Type of column */
    type: "button";

    /* Button label */
    label: string;

    /* On Click action */
    onClick: (row: any) => void;
}

export interface ICheckColumn extends IColumn {
    /* Type of column */
    type: "check";

    selector: string | ((row) => boolean);

    onClick: (row: unknown) => void;
}

interface ITableProps {
    /**
     * The table columns
     * @param type string
     * @param displayName
     * @param width number
     * @param minWidth `optional` `number`
     * @param columnKey `number` A unique identifier tied to a column
     * @param customerReOrder: `optional` `(value: unknown) => void` Implement specific reorder logic to this column
     * @param disableReOrder `optional` specifically disable reordering for a specific column
     */
    columns: (IValueColumn | IMenuColumn | IButtonColumn | ICheckColumn)[];

    /* The table rows */
    rows: any[];

    /** Default checked rows
     *  Takes the index that should start of selected
     **/
    defaultChecked?: number[];

    /* Index of selected row */
    selectedRow?: number;

    /* Handler on row click */
    onRowClick?: (row: any, rowIndex: number) => void;

    /* Whether the table is hidden fromview */
    hidden?: boolean;

    /* Disable column resizing */
    disableResizeColumns?: boolean;

    canReOrder?: boolean;
}

export interface IOrderStatus {
    columnId: number;
    orderBy: SortOption;
}

export interface IRowWithIndexAndSelected {
    index: number;
    isSelected: boolean;
    row: any;
}

/**
 * Creates a table to show data.
 * @param props Configuration for the table
 * - columns: `IColumn[]` See `IColumn`
 * - rows: `any[]`
 * - selectedRow?: `number` Can be set to have a default selected row
 * - onRowClick?: `(row, rowIndex) => void` Have custom onclick functionality
 * - hidden?: `boolean`
 * - canReOrder?: `boolean` By default is set to false
 */
export const Table: React.FunctionComponent<ITableProps> = (props) => {
    React.useEffect(() => {
        props.columns.forEach((c) => {
            if (!c.minWidth) {
                c.minWidth = c.width;
            }
        });
    }, [props.columns]);

    // "stylingClassName" is used for "styled components" extensions. It is hidden from the developer.
    const stylingClassName = (props as any).className
        ? (props as any).className
        : "",
        [columnWidth, setColumnWidth] = React.useState<number[]>(props.columns.map(col => col.width)),
        [order, setOrder] = React.useState<IOrderStatus>({
            columnId: -1,
            orderBy: 0,
        });

    const tableRef = React.useRef<VariableSizeGrid>(),
        headerRef = React.createRef<HTMLDivElement>();

    const getDefaultOrder = (word: string) => {
        return word.toString().toLowerCase();
    };

    const defaultOrder = (column: string, row: unknown) => {
        if (isNumber(row[column])) {
            return row[column] as number;
        }

        return isNull(row[column]) || isEmpty(row[column])
            ? Number.MIN_SAFE_INTEGER
            : getDefaultOrder(row[column]);
    };

    const setReOrder = (columnid: number) => {
        const clickedColumn = find(props.columns, { columnKey: columnid });

        // Don't sort if not a value
        //      could probably sort anyway using custom order
        if (
            clickedColumn.type !== `value` ||
            clickedColumn.disableReOrder === true
        ) {
            return;
        }

        let newOrderBy: number;

        if (order.columnId !== columnid) {
            newOrderBy = 1;
        } else {
            newOrderBy = order.orderBy + 1 === 3 ? 0 : order.orderBy + 1;
        }

        setOrder({ columnId: columnid, orderBy: newOrderBy });
    };

    const getRows = (rows: any[]): IRowWithIndexAndSelected[] => {
        const orderingColumn = find(props.columns, {
            columnKey: order.columnId,
        });

        const canReorder =
            props.canReOrder === undefined ? false : props.canReOrder;

        const rowsWithIndexAndSelected = props.rows.map((row, index) => {
            return {
                index,
                isSelected: props.selectedRow === index,
                row,
            };
        });

        if (
            !canReorder ||
            orderingColumn === undefined ||
            orderingColumn.disableReOrder === true ||
            orderingColumn.type !== `value`
        ) {
            return rowsWithIndexAndSelected;
        }

        switch (order.orderBy) {
            case SortOption.default:
                return rowsWithIndexAndSelected;

            case SortOption.asc:
                if (orderingColumn.customReOrder !== undefined) {
                    return sortBy(rowsWithIndexAndSelected, [(rowWithIndexAndSelected) => orderingColumn.customReOrder(rowWithIndexAndSelected.row),]);
                }
                return sortBy(rowsWithIndexAndSelected, [(rowWithIndexAndSelect) => defaultOrder(orderingColumn.selector.toString(), rowWithIndexAndSelect.row),]);

            case SortOption.dsc:
                if (orderingColumn.customReOrder !== undefined) {
                    return reverse(sortBy(rowsWithIndexAndSelected, [(rowWithIndexAndSelected) => orderingColumn.customReOrder(rowWithIndexAndSelected.row),]));
                }
                return reverse(sortBy(rowsWithIndexAndSelected, [(rowWithIndexAndSelect) => defaultOrder(orderingColumn.selector.toString(), rowWithIndexAndSelect.row),]));

            default:
                throw new Error(`This should only ever be reached if the orderBy is somehow not 0,1,2`);
        }
    };

    const rowMemo = useMemo(
        () => getRows(props.rows),
        [order.orderBy, order.columnId, props.rows, props.selectedRow]
    );

    const onRowClick = (rowIndex: number) => {
        if (props.onRowClick) {
            props.onRowClick(rowMemo.find(fi => fi.index === rowIndex), rowIndex);
        }
    };

    const resizeColumns = (columnIndex: number, width: number): void => {
        let newColumnsList: number[] = [...columnWidth];
        newColumnsList[columnIndex] = width;
        setColumnWidth(newColumnsList);
        tableRef.current.resetAfterColumnIndex(0, false);
    };

    const columns = props.columns.map((col, index) => ({ ...col, width: columnWidth[index] }));

    return (
        <TableDiv className={stylingClassName} theme={{ hidden: props.hidden }}>
            {props.canReOrder ? (
                <TableHeader
                    containerRef={headerRef}
                    columns={columns}
                    resizeColumns={resizeColumns}
                    disableResize={props.disableResizeColumns}
                    onReorder={setReOrder}
                    orderStatus={order}
                />
            ) : (
                <TableHeader
                    containerRef={headerRef}
                    columns={columns}
                    resizeColumns={resizeColumns}
                    disableResize={props.disableResizeColumns}
                />
            )}

            <TableBody
                tableRef={tableRef}
                columns={columns}
                rows={rowMemo}
                onHScroll={(scrollLeft) =>
                    (headerRef.current.scrollLeft = scrollLeft)
                }
                selectable={props.onRowClick != null}
                selectedRow={rowMemo.findIndex(
                    (rowWithSelected) => rowWithSelected.isSelected
                )}
                onRowClick={onRowClick}
            />
        </TableDiv>
    );
};
